// Tiny typed fetch wrapper for the API. Throws on !ok.
//
// Reads (GETs) use jsonFetch — no auth.
// Writes (POST/PUT) use adminFetch — adds Authorization: Bearer <admin key>
// from localStorage. The debug panel (Ctrl+D in the UI) lets the user paste
// and persist that key.

import { useEffect, useState } from "react";
import type {
  Company,
  CountBucket,
  FilterPreset,
  Granularity,
  QuerySpec,
  UserQuery,
  UserQueryItem,
} from "../shared/types";

const ADMIN_KEY_STORAGE = "mindsharex.adminkey";
// Same-tab notifier for setAdminKey/clearAdminKey. The browser's `storage`
// event only fires in OTHER tabs, so we emit our own here for the active tab.
const ADMIN_KEY_CHANGE_EVENT = "mindsharex:adminkey-change";

export function getAdminKey(): string | null {
  try {
    return localStorage.getItem(ADMIN_KEY_STORAGE);
  } catch {
    return null;
  }
}

export function setAdminKey(key: string): void {
  try {
    if (key) localStorage.setItem(ADMIN_KEY_STORAGE, key);
    else localStorage.removeItem(ADMIN_KEY_STORAGE);
    window.dispatchEvent(new Event(ADMIN_KEY_CHANGE_EVENT));
  } catch {
    /* ignore */
  }
}

export function clearAdminKey(): void {
  try {
    localStorage.removeItem(ADMIN_KEY_STORAGE);
    window.dispatchEvent(new Event(ADMIN_KEY_CHANGE_EVENT));
  } catch {
    /* ignore */
  }
}

// Reactive boolean: is an admin key currently set? Re-renders when the
// DebugPanel saves/clears the key (custom event) or another tab changes it
// (`storage` event). Use to hide admin-only UI from public viewers.
export function useHasAdminKey(): boolean {
  const [has, setHas] = useState<boolean>(() => !!getAdminKey());
  useEffect(() => {
    const update = () => setHas(!!getAdminKey());
    window.addEventListener(ADMIN_KEY_CHANGE_EVENT, update);
    window.addEventListener("storage", update);
    return () => {
      window.removeEventListener(ADMIN_KEY_CHANGE_EVENT, update);
      window.removeEventListener("storage", update);
    };
  }, []);
  return has;
}

export class AdminRequiredError extends Error {
  constructor(message = "admin key required — open debug panel (Ctrl+D)") {
    super(message);
    this.name = "AdminRequiredError";
  }
}

async function jsonFetch<T>(input: RequestInfo, init?: RequestInit): Promise<T> {
  const r = await fetch(input, init);
  if (!r.ok) {
    const text = await r.text();
    throw new Error(`HTTP ${r.status}: ${text || r.statusText}`);
  }
  return (await r.json()) as T;
}

async function adminFetch<T>(input: RequestInfo, init: RequestInit = {}): Promise<T> {
  const key = getAdminKey();
  const headers = new Headers(init.headers);
  if (key) headers.set("Authorization", `Bearer ${key}`);
  const r = await fetch(input, { ...init, headers });
  if (r.status === 401) throw new AdminRequiredError();
  if (!r.ok) {
    const text = await r.text();
    throw new Error(`HTTP ${r.status}: ${text || r.statusText}`);
  }
  return (await r.json()) as T;
}

export interface DashboardResponse {
  range_start: number;
  range_end: number;
  granularity: Granularity;
  preset: FilterPreset;
  items: Array<{
    company: Company;
    spec: QuerySpec;
    buckets: CountBucket[];
  }>;
  user_query_items: UserQueryItem[];
}

export const api = {
  dashboard(windowDays = 30): Promise<DashboardResponse> {
    return jsonFetch(`/api/dashboard?window_days=${windowDays}`);
  },

  companies(): Promise<{
    companies: Array<
      Company & {
        specs: Array<QuerySpec & { filter_preset_name: string }>;
      }
    >;
  }> {
    return jsonFetch("/api/companies");
  },

  filterPresets(): Promise<{ presets: FilterPreset[] }> {
    return jsonFetch("/api/filter-presets");
  },

  createFilterPreset(
    name: string,
    filterClause: string,
  ): Promise<{ preset: FilterPreset }> {
    return adminFetch("/api/filter-presets", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, filter_clause: filterClause }),
    });
  },

  upsertSpec(companyId: number, filterPresetId?: number): Promise<{ spec: QuerySpec }> {
    return adminFetch("/api/specs", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        company_id: companyId,
        filter_preset_id: filterPresetId,
      }),
    });
  },

  series(
    hash: string,
    start: number,
    end: number,
    granularity: Granularity = "hour",
  ): Promise<{
    query_hash: string;
    full_query: string;
    granularity: Granularity;
    range_start: number;
    range_end: number;
    buckets: CountBucket[];
    missing_count: number;
  }> {
    const u = new URL("/api/series", location.origin);
    u.searchParams.set("hash", hash);
    u.searchParams.set("start", String(start));
    u.searchParams.set("end", String(end));
    u.searchParams.set("granularity", granularity);
    return jsonFetch(u.pathname + u.search);
  },

  gapCost(
    hash: string,
    start: number,
    end: number,
    granularity: Granularity = "hour",
  ): Promise<{
    api_calls: number;
    cost_mills: number;
    buckets_to_backfill: number;
    gaps: number;
    endpoint: "all" | "recent";
  }> {
    const u = new URL("/api/sync/gap-cost", location.origin);
    u.searchParams.set("hash", hash);
    u.searchParams.set("start", String(start));
    u.searchParams.set("end", String(end));
    u.searchParams.set("granularity", granularity);
    return jsonFetch(u.pathname + u.search);
  },

  sync(
    hash: string,
    start: number,
    end: number,
    granularity: Granularity = "hour",
  ): Promise<{
    buckets_written: number;
    api_calls: number;
    cost_mills: number;
    errors: string[];
  }> {
    return adminFetch("/api/sync", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ hash, start, end, granularity }),
    });
  },

  cost(sinceSec?: number): Promise<{
    total_calls: number;
    total_mills: number;
    ok_calls: number;
    failed_calls: number;
    since: number;
  }> {
    const u = new URL("/api/cost", location.origin);
    if (sinceSec !== undefined) u.searchParams.set("since", String(sinceSec));
    return jsonFetch(u.pathname + u.search);
  },

  listUserQueries(): Promise<{ user_queries: UserQuery[] }> {
    return jsonFetch("/api/user-queries");
  },

  createUserQuery(
    label: string,
    rawQuery: string,
  ): Promise<{
    user_query: UserQuery;
    backfill: { buckets_written: number; api_calls: number; cost_mills: number; errors: string[] } | null;
  }> {
    return adminFetch("/api/user-queries", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ label, raw_query: rawQuery }),
    });
  },

  deleteUserQuery(id: number): Promise<{ ok: boolean }> {
    return adminFetch(`/api/user-queries/${id}`, { method: "DELETE" });
  },

  // Fill any gaps in the last N days for every active spec.
  //   7d (default) → /counts/recent, ~$0.005/spec
  //   >7d         → /counts/all (paginated ~7d per call), ~$0.01/call
  // Used by the toolbar SYNC 7d (the normal case) and the DebugPanel "30d
  // backfill" button (the backup when 7d sync missed gaps that have aged out).
  cronRun(lookbackDays?: number): Promise<{
    specs: number;
    api_calls: number;
    cost_mills: number;
    errors: string[];
    lookback_days: number;
  }> {
    const u = new URL("/api/cron/run", location.origin);
    if (lookbackDays !== undefined) {
      u.searchParams.set("lookback_days", String(lookbackDays));
    }
    return adminFetch(u.pathname + u.search, { method: "POST" });
  },
};

// Format mill cost ($0.001 units) as a USD string. 4-decimal under a cent so
// /counts/recent calls render as $0.0050 instead of being squashed to $0.01.
export function fmtMillsUsd(mills: number): string {
  const dollars = mills / 1000;
  if (dollars > 0 && dollars < 0.01) return `$${dollars.toFixed(4)}`;
  return `$${dollars.toFixed(2)}`;
}
